import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, Brain, Target, Zap, ChevronRight, 
  CheckCircle, Clock, BookOpen, Trash2, Play,
  RotateCcw, Trophy, Settings, ArrowLeft, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UK_STAGES = [
  { id: '7plus', name: '7+', description: 'Year 2-3' },
  { id: '11plus', name: '11+', description: 'Year 5-6' },
  { id: 'ks3', name: 'KS3', description: 'Year 7-9' },
  { id: 'gcse', name: 'GCSE', description: 'Year 10-11' },
  { id: 'a-level', name: 'A-Level', description: 'Year 12-13' },
];

const SUBJECTS = [
  { id: 'all', name: 'All Subjects', color: 'text-purple-600' },
  { id: 'maths', name: 'Maths', color: 'text-blue-600' },
  { id: 'english', name: 'English', color: 'text-green-600' },
  { id: 'verbal_reasoning', name: 'Verbal Reasoning', color: 'text-amber-600' },
  { id: 'non_verbal_reasoning', name: 'Non-Verbal Reasoning', color: 'text-pink-600' },
];

const DIFFICULTY_LABELS = {
  1: 'Very Easy',
  2: 'Easy',
  3: 'Medium',
  4: 'Hard',
  5: 'Very Hard'
};

const DIFFICULTY_COLORS = {
  1: 'bg-green-100 text-green-700',
  2: 'bg-emerald-100 text-emerald-700',
  3: 'bg-yellow-100 text-yellow-700',
  4: 'bg-orange-100 text-orange-700',
  5: 'bg-red-100 text-red-700'
};

const CustomPracticeGenerator = () => {
  const navigate = useNavigate();
  const [selectedStage, setSelectedStage] = useState('11plus');
  const [selectedSubject, setSelectedSubject] = useState('maths');
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [difficulty, setDifficulty] = useState([3]);
  const [questionsPerTopic, setQuestionsPerTopic] = useState(5);
  const [paperName, setPaperName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [myPapers, setMyPapers] = useState([]);
  const [showGenerator, setShowGenerator] = useState(true);
  const [weakTopics, setWeakTopics] = useState([]);
  const [loadingWeakTopics, setLoadingWeakTopics] = useState(false);
  const [recommendedTopicNames, setRecommendedTopicNames] = useState(new Set());
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [subjectMessage, setSubjectMessage] = useState('');

  const token = localStorage.getItem('token');

  // Get user ID from token
  const getUserId = useCallback(() => {
    try {
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.user_id;
    } catch {
      return null;
    }
  }, [token]);

  // Normalize topic name for comparison
  const normalizeTopic = (name) => {
    return name.toLowerCase().replace(/[\s_-]+/g, '').replace(/[^a-z0-9]/g, '');
  };

  // Check if a topic matches any weak topic
  const isWeakTopic = useCallback((topicName) => {
    const normalizedName = normalizeTopic(topicName);
    return weakTopics.some(wt => {
      const normalizedWeak = normalizeTopic(wt.topic_name);
      const normalizedWeakId = normalizeTopic(wt.topic);
      return normalizedName === normalizedWeak || 
             normalizedName === normalizedWeakId ||
             normalizedName.includes(normalizedWeak) ||
             normalizedWeak.includes(normalizedName);
    });
  }, [weakTopics]);

  // Load weak topics from learning engine
  const loadWeakTopics = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    
    setLoadingWeakTopics(true);
    try {
      const response = await fetch(`${API_URL}/api/learning-engine/growth-zone/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.has_data && data.focus_topics && data.focus_topics.length > 0) {
        setWeakTopics(data.focus_topics);
        // Store normalized weak topic names for quick lookup
        const weakSet = new Set();
        data.focus_topics.forEach(ft => {
          weakSet.add(normalizeTopic(ft.topic_name));
          weakSet.add(normalizeTopic(ft.topic));
        });
        setRecommendedTopicNames(weakSet);
        toast.success(`Found ${data.focus_topics.length} topics to focus on!`, {
          description: 'Based on your assessment results'
        });
      }
    } catch (error) {
      console.error('Error loading weak topics:', error);
    } finally {
      setLoadingWeakTopics(false);
    }
  }, [getUserId, token]);

  // Load topics when stage or subject changes
  useEffect(() => {
    loadTopics();
    setHasAutoSelected(false); // Reset auto-selection when stage/subject changes
  }, [selectedStage, selectedSubject]);

  // Load weak topics after topics are loaded
  useEffect(() => {
    if (topics.length > 0) {
      loadWeakTopics();
    }
  }, [topics, loadWeakTopics]);

  // Auto-select weak topics when both topics and weak topics are loaded
  useEffect(() => {
    if (topics.length > 0 && weakTopics.length > 0 && !hasAutoSelected) {
      // Filter weak topics by current subject
      const relevantWeakTopics = selectedSubject === 'all' 
        ? weakTopics 
        : weakTopics.filter(wt => wt.subject === selectedSubject);
      
      const matchingTopics = topics.filter(t => {
        const normalizedName = normalizeTopic(t.name);
        return relevantWeakTopics.some(wt => {
          const normalizedWeak = normalizeTopic(wt.topic_name);
          const normalizedWeakId = normalizeTopic(wt.topic);
          return normalizedName === normalizedWeak || 
                 normalizedName === normalizedWeakId ||
                 normalizedName.includes(normalizedWeak) ||
                 normalizedWeak.includes(normalizedName);
        });
      });
      
      if (matchingTopics.length > 0) {
        setSelectedTopics(matchingTopics.map(t => t.name));
        setHasAutoSelected(true);
      }
    }
  }, [topics, weakTopics, hasAutoSelected, selectedSubject]);

  // Load user's saved papers
  useEffect(() => {
    loadMyPapers();
  }, []);

  const loadTopics = async () => {
    setLoadingTopics(true);
    setSubjectMessage('');
    try {
      // For 'all' subjects, load maths as default (can expand later)
      const subjectToLoad = selectedSubject === 'all' ? 'maths' : selectedSubject;
      const response = await fetch(`${API_URL}/api/ai-practice/topics/${selectedStage}?subject=${subjectToLoad}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTopics(data.topics || []);
      setAvailableSubjects(data.availableSubjects || []);
      setSelectedTopics([]); // Reset selection when stage/subject changes
      
      // Show message if no topics available
      if (data.message && (!data.topics || data.topics.length === 0)) {
        setSubjectMessage(data.message);
      }
    } catch (error) {
      console.error('Error loading topics:', error);
      toast.error('Failed to load topics');
    } finally {
      setLoadingTopics(false);
    }
  };

  const loadMyPapers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ai-practice/my-papers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setMyPapers(data.papers || []);
    } catch (error) {
      console.error('Error loading papers:', error);
    }
  };

  const toggleTopic = (topicName) => {
    setSelectedTopics(prev => 
      prev.includes(topicName) 
        ? prev.filter(t => t !== topicName)
        : [...prev, topicName]
    );
  };

  const selectAllTopics = () => {
    setSelectedTopics(topics.map(t => t.name));
  };

  const clearAllTopics = () => {
    setSelectedTopics([]);
  };

  const generatePaper = async () => {
    if (selectedTopics.length === 0) {
      toast.error('Please select at least one topic');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ai-practice/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          stage: selectedStage,
          subject: selectedSubject === 'all' ? 'maths' : selectedSubject,
          topics: selectedTopics,
          difficulty: difficulty[0],
          questionsPerTopic,
          name: paperName || `Custom Practice - ${new Date().toLocaleDateString()}`
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Created paper with ${data.totalQuestions} questions!`);
        loadMyPapers();
        // Navigate to the paper
        navigate(`/ai-practice/${data.paperId}`);
      } else {
        toast.error(data.detail || 'Failed to generate paper');
      }
    } catch (error) {
      console.error('Error generating paper:', error);
      toast.error('Failed to generate paper');
    } finally {
      setLoading(false);
    }
  };

  const deletePaper = async (paperId) => {
    if (!window.confirm('Are you sure you want to delete this paper?')) return;

    try {
      const response = await fetch(`${API_URL}/api/ai-practice/paper/${paperId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Paper deleted');
        loadMyPapers();
      }
    } catch (error) {
      toast.error('Failed to delete paper');
    }
  };

  const totalQuestions = selectedTopics.length * questionsPerTopic;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate(-1)}
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Sparkles className="h-7 w-7 text-purple-600" />
                  My Growth Zone
                </h1>
                <p className="text-gray-500 text-sm">Turn gaps into strengths</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant={showGenerator ? "default" : "outline"}
                onClick={() => setShowGenerator(true)}
                data-testid="show-generator"
              >
                <Settings className="h-4 w-4 mr-2" />
                Create New
              </Button>
              <Button 
                variant={!showGenerator ? "default" : "outline"}
                onClick={() => setShowGenerator(false)}
                data-testid="show-my-papers"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                My Papers ({myPapers.length})
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {showGenerator ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Settings Panel */}
            <div className="lg:col-span-1 space-y-4">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-600" />
                    Paper Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Paper Name */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Paper Name (optional)
                    </label>
                    <input
                      type="text"
                      value={paperName}
                      onChange={(e) => setPaperName(e.target.value)}
                      placeholder="My Custom Practice"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      data-testid="paper-name-input"
                    />
                  </div>

                  {/* Year Group */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Year Group
                    </label>
                    <Select value={selectedStage} onValueChange={setSelectedStage}>
                      <SelectTrigger data-testid="stage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UK_STAGES.map(stage => (
                          <SelectItem key={stage.id} value={stage.id}>
                            {stage.name} ({stage.description})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subject Selection - Radio Buttons */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-3">
                      Subject
                    </label>
                    <div className="space-y-2">
                      {SUBJECTS.map(subject => (
                        <label 
                          key={subject.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                            selectedSubject === subject.id 
                              ? 'bg-purple-50 border border-purple-200' 
                              : 'hover:bg-gray-50'
                          }`}
                          data-testid={`subject-${subject.id}`}
                        >
                          <input
                            type="radio"
                            name="subject"
                            value={subject.id}
                            checked={selectedSubject === subject.id}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                          />
                          <span className={`text-sm font-medium ${subject.color}`}>
                            {subject.name}
                          </span>
                          {subject.id !== 'all' && availableSubjects.includes(subject.id) && (
                            <Badge variant="outline" className="text-xs ml-auto">Available</Badge>
                          )}
                          {subject.id !== 'all' && !availableSubjects.includes(subject.id) && availableSubjects.length > 0 && (
                            <Badge variant="outline" className="text-xs ml-auto text-gray-400">Coming Soon</Badge>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Difficulty Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Difficulty Level
                      </label>
                      <Badge className={DIFFICULTY_COLORS[difficulty[0]]}>
                        {DIFFICULTY_LABELS[difficulty[0]]}
                      </Badge>
                    </div>
                    <Slider
                      value={difficulty}
                      onValueChange={setDifficulty}
                      min={1}
                      max={5}
                      step={1}
                      className="mt-2"
                      data-testid="difficulty-slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>Easy</span>
                      <span>Hard</span>
                    </div>
                  </div>

                  {/* Questions Per Topic */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Questions Per Topic
                    </label>
                    <Select 
                      value={questionsPerTopic.toString()} 
                      onValueChange={(v) => setQuestionsPerTopic(parseInt(v))}
                    >
                      <SelectTrigger data-testid="questions-per-topic">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 questions</SelectItem>
                        <SelectItem value="5">5 questions</SelectItem>
                        <SelectItem value="7">7 questions</SelectItem>
                        <SelectItem value="10">10 questions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Summary */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-medium text-purple-900 mb-2">Paper Summary</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Topics selected:</span>
                        <span className="font-medium">{selectedTopics.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Questions per topic:</span>
                        <span className="font-medium">{questionsPerTopic}</span>
                      </div>
                      <div className="flex justify-between text-purple-700 font-medium pt-2 border-t border-purple-200">
                        <span>Total questions:</span>
                        <span>{totalQuestions}</span>
                      </div>
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={generatePaper}
                    disabled={loading || selectedTopics.length === 0}
                    data-testid="generate-paper-btn"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Paper
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Topic Selection Panel */}
            <div className="lg:col-span-2">
              <Card className="shadow-lg">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="h-5 w-5 text-blue-600" />
                        Select Topics
                      </CardTitle>
                      <CardDescription>
                        Choose topics you want to practice
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {weakTopics.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                          onClick={() => {
                            const matchingTopics = topics.filter(t => isWeakTopic(t.name));
                            setSelectedTopics(matchingTopics.map(t => t.name));
                          }}
                          data-testid="select-recommended"
                        >
                          <AlertTriangle className="h-4 w-4 mr-1" />
                          Select Recommended
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={selectAllTopics}
                        data-testid="select-all-topics"
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={clearAllTopics}
                        data-testid="clear-all-topics"
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  
                  {/* Weak Topics Banner */}
                  {weakTopics.length > 0 && (() => {
                    // Filter weak topics by selected subject
                    const relevantWeakTopics = selectedSubject === 'all' 
                      ? weakTopics 
                      : weakTopics.filter(wt => wt.subject === selectedSubject);
                    
                    // Check if any weak topics match current subject
                    const matchingWeakInCurrentSubject = topics.filter(t => isWeakTopic(t.name)).length;
                    
                    // If selected subject has no matching weak topics but other subjects do
                    if (matchingWeakInCurrentSubject === 0 && relevantWeakTopics.length === 0 && weakTopics.length > 0) {
                      const subjectsWithWeakTopics = [...new Set(weakTopics.map(wt => wt.subject))];
                      return (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Target className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-blue-800">
                                You have weak topics in other subjects
                              </p>
                              <p className="text-xs text-blue-600 mt-0.5">
                                Select {subjectsWithWeakTopics.map(s => SUBJECTS.find(sub => sub.id === s)?.name || s).join(', ')} to practice your focus areas.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    if (matchingWeakInCurrentSubject === 0) return null;
                    
                    return (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-amber-800">
                              {matchingWeakInCurrentSubject} topic{matchingWeakInCurrentSubject > 1 ? 's' : ''} need{matchingWeakInCurrentSubject === 1 ? 's' : ''} attention
                            </p>
                            <p className="text-xs text-amber-600 mt-0.5">
                              Based on your recent assessments. Topics marked with ★ are recommended for practice.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardHeader>
                <CardContent>
                  {loadingTopics || loadingWeakTopics ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full" />
                    </div>
                  ) : topics.length === 0 ? (
                    <div className="text-center py-12">
                      <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600 font-medium mb-2">
                        No {selectedSubject === 'all' ? '' : SUBJECTS.find(s => s.id === selectedSubject)?.name + ' '}topics available yet
                      </p>
                      <p className="text-gray-500 text-sm mb-4">
                        {subjectMessage || `${SUBJECTS.find(s => s.id === selectedSubject)?.name || selectedSubject} questions for ${UK_STAGES.find(s => s.id === selectedStage)?.name} are coming soon!`}
                      </p>
                      {availableSubjects.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-500 mb-2">Available subjects for this year group:</p>
                          <div className="flex justify-center gap-2 flex-wrap">
                            {availableSubjects.map(subj => (
                              <Button 
                                key={subj}
                                variant="outline" 
                                size="sm"
                                onClick={() => setSelectedSubject(subj)}
                                data-testid={`switch-to-${subj}`}
                              >
                                {SUBJECTS.find(s => s.id === subj)?.name || subj}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {topics.map((topic) => {
                        const isSelected = selectedTopics.includes(topic.name);
                        const isRecommended = isWeakTopic(topic.name);
                        const weakTopicData = weakTopics.find(wt => 
                          normalizeTopic(wt.topic_name) === normalizeTopic(topic.name) ||
                          normalizeTopic(wt.topic) === normalizeTopic(topic.name)
                        );
                        
                        return (
                          <div
                            key={topic.name}
                            className={`
                              relative p-4 rounded-xl border-2 cursor-pointer transition-all
                              ${isSelected 
                                ? 'border-purple-500 bg-purple-50' 
                                : isRecommended
                                  ? 'border-amber-300 bg-amber-50/50 hover:border-amber-400'
                                  : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                              }
                            `}
                            onClick={() => toggleTopic(topic.name)}
                            data-testid={`topic-${topic.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox 
                                checked={isSelected}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-gray-900">{topic.name}</h4>
                                  {isRecommended && (
                                    <span className="text-amber-500 text-sm" title="Recommended for practice">★</span>
                                  )}
                                </div>
                                {isRecommended && weakTopicData && (
                                  <p className="text-xs text-amber-600 mt-1">
                                    Score: {weakTopicData.avg_score}% • Needs practice
                                  </p>
                                )}
                              </div>
                            </div>
                            {isSelected && (
                              <div className="absolute top-2 right-2">
                                <CheckCircle className="h-5 w-5 text-purple-600" />
                              </div>
                            )}
                            {isRecommended && !isSelected && (
                              <Badge 
                                className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-xs"
                                data-testid={`recommended-badge-${topic.name.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                Focus
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* My Papers Section */
          <div>
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-purple-600" />
                  My Saved Papers
                </CardTitle>
                <CardDescription>
                  Your previously generated custom practice papers
                </CardDescription>
              </CardHeader>
              <CardContent>
                {myPapers.length === 0 ? (
                  <div className="text-center py-12">
                    <Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No custom papers yet</p>
                    <Button 
                      className="mt-4"
                      onClick={() => setShowGenerator(true)}
                    >
                      Create Your First Paper
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {myPapers.map((paper) => (
                      <Card 
                        key={paper.id} 
                        className="hover:shadow-md transition-shadow"
                        data-testid={`saved-paper-${paper.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-medium text-gray-900">{paper.name}</h4>
                              <p className="text-xs text-gray-500">
                                {new Date(paper.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className={DIFFICULTY_COLORS[paper.difficulty]}>
                              {DIFFICULTY_LABELS[paper.difficulty]}
                            </Badge>
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Target className="h-4 w-4" />
                              <span>{paper.topics?.length || 0} topics</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <BookOpen className="h-4 w-4" />
                              <span>{paper.totalQuestions} questions</span>
                            </div>
                            {paper.best_score !== null && (
                              <div className="flex items-center gap-2 text-purple-600">
                                <Trophy className="h-4 w-4" />
                                <span>Best: {paper.best_score}%</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-gray-500">
                              <RotateCcw className="h-4 w-4" />
                              <span>{paper.attempts || 0} attempts</span>
                            </div>
                          </div>

                          <div className="flex gap-2 mt-4">
                            <Button 
                              className="flex-1 bg-purple-600 hover:bg-purple-700"
                              onClick={() => navigate(`/ai-practice/${paper.id}`)}
                              data-testid={`start-paper-${paper.id}`}
                            >
                              <Play className="h-4 w-4 mr-1" />
                              {paper.attempts > 0 ? 'Retry' : 'Start'}
                            </Button>
                            <Button 
                              variant="outline"
                              size="icon"
                              onClick={() => deletePaper(paper.id)}
                              data-testid={`delete-paper-${paper.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomPracticeGenerator;
